import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import KitReviews, { Stars } from "@/components/KitReviews";
import ShareButtons from "@/components/ShareButtons";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Package, Clock, Wrench, ShoppingCart, ShieldCheck, Truck, BookOpen, Loader2 } from "lucide-react";

const CATEGORY_LABELS = {
  heating: "Chauffage",
  pump: "Pompe",
  electronics: "Électronique",
  water: "Traitement eau",
  filter: "Filtration",
  other: "Autres",
};

const DIFFICULTY_STYLES = {
  easy: { label: "Facile", cls: "bg-emerald-100 text-emerald-800" },
  medium: { label: "Intermédiaire", cls: "bg-amber-100 text-amber-800" },
  hard: { label: "Expert", cls: "bg-red-100 text-red-800" },
};

export default function KitDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kit, setKit] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/kits/${slug}`).catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
        return null;
      }),
      api.get(`/kits/shipping/tiers`).catch(() => ({ data: [] })),
    ])
      .then(([kitRes, tiersRes]) => {
        if (kitRes) setKit(kitRes.data);
        setTiers(tiersRes.data || []);
        if ((tiersRes.data || []).length > 0) setSelectedTier(tiersRes.data[0].id);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [slug]);

  useSEO(
    kit
      ? {
          title: `${kit.name} — Kit spa | DoctoSpa`,
          description: kit.short_description,
          image: kit.cover_photo_url ? `${backend}${kit.cover_photo_url}` : undefined,
          url: `https://doctospa.fr/kits/${kit.slug}`,
          type: "product",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            name: kit.name,
            description: kit.short_description,
            sku: kit.slug,
            image: (kit.gallery_photos || [])
              .slice(0, 4)
              .map((p) => `${backend}${p.url}`)
              .filter(Boolean),
            brand: { "@type": "Brand", name: kit.spa_brand || "DoctoSpa" },
            offers: {
              "@type": "Offer",
              url: `https://doctospa.fr/kits/${kit.slug}`,
              priceCurrency: "EUR",
              price: (kit.price_cents / 100).toFixed(2),
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/NewCondition",
            },
            ...(kit.reviews_count > 0
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: kit.rating_avg,
                    reviewCount: kit.reviews_count,
                    bestRating: 5,
                    worstRating: 1,
                  },
                }
              : {}),
          },
        }
      : { title: "Kit — DoctoSpa" }
  );

  const onCheckout = async () => {
    if (!selectedTier) {
      toast.error("Choisissez un mode de livraison");
      return;
    }
    setCheckoutLoading(true);
    try {
      const r = await api.post(`/kits/${slug}/checkout`, {
        origin_url: window.location.origin,
        quantity,
        shipping_tier_id: selectedTier,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur checkout");
      setCheckoutLoading(false);
    }
  };

  if (loading) return <p className="mx-auto max-w-4xl px-6 py-16 text-sm text-muted-foreground">Chargement…</p>;
  if (notFound || !kit) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <h1 className="font-display text-2xl font-bold">Kit introuvable</h1>
        <Link to="/kits">
          <Button className="mt-6 rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> Tous les kits</Button>
        </Link>
      </div>
    );
  }

  const diff = DIFFICULTY_STYLES[kit.difficulty] || DIFFICULTY_STYLES.medium;
  const selectedTierObj = tiers.find((t) => t.id === selectedTier);
  const total = ((kit.price_cents * quantity) + (selectedTierObj?.amount_cents || 0)) / 100;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12 lg:py-16" data-testid="kit-detail-page">
      <Link to="/kits" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Tous les kits
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Left: photos */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900">
            {kit.cover_photo_url ? (
              <img src={`${backend}${kit.cover_photo_url}`} alt={kit.name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full grid place-items-center">
                <Package className="h-24 w-24 text-white/40" strokeWidth={1.2} />
              </div>
            )}
          </div>
          {(kit.gallery_photos || []).length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {kit.gallery_photos.slice(0, 4).map((p, i) => (
                <div key={p.path} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                  <img src={`${backend}${p.url}`} alt={p.filename} className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: info + CTA */}
        <div>
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="secondary" className="rounded-full">{CATEGORY_LABELS[kit.category] || kit.category}</Badge>
            {kit.spa_brand && <Badge variant="outline" className="rounded-full">{kit.spa_brand}</Badge>}
            <Badge className={`rounded-full border-0 ${diff.cls}`}>{diff.label}</Badge>
          </div>

          <h1 className="mt-3 font-display text-3xl lg:text-4xl font-black tracking-tight leading-tight">
            {kit.name}
          </h1>
          <p className="mt-3 text-muted-foreground leading-relaxed">{kit.short_description}</p>

          {kit.reviews_count > 0 && (
            <div className="mt-3 flex items-center gap-2" data-testid="kit-rating-summary">
              <Stars value={kit.rating_avg} />
              <span className="text-sm font-semibold">{kit.rating_avg}/5</span>
              <span className="text-sm text-muted-foreground">({kit.reviews_count} avis)</span>
            </div>
          )}

          <div className="mt-5 flex items-center gap-6 text-sm">
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" /> {kit.estimated_time_min} min</span>
            <span className="flex items-center gap-1.5"><Package className="h-4 w-4 text-secondary" /> {kit.weight_grams} g</span>
          </div>

          <div className="mt-6 font-display text-4xl font-black text-primary tracking-tight">
            {kit.price_display}
          </div>
          <p className="text-xs text-muted-foreground">TVA 20 % incluse — livraison en sus</p>

          {/* Checkout box */}
          <Card className="mt-6 p-5 border-2 border-primary/20" data-testid="checkout-box">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide">Quantité</label>
                <div className="mt-1 flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setQuantity(Math.max(1, quantity - 1))} data-testid="btn-qty-minus">−</Button>
                  <span className="min-w-[3ch] text-center font-mono" data-testid="qty-value">{quantity}</span>
                  <Button variant="outline" size="sm" onClick={() => setQuantity(Math.min(10, quantity + 1))} data-testid="btn-qty-plus">+</Button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide">Livraison</label>
                <Select value={selectedTier} onValueChange={setSelectedTier}>
                  <SelectTrigger className="mt-1" data-testid="shipping-tier-select">
                    <SelectValue placeholder="Choisir…" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`shipping-tier-${t.id}`}>
                        {t.name} — {t.amount_display} ({t.delivery_min_days}-{t.delivery_max_days} j)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-2 border-t flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-display text-2xl font-black" data-testid="total-price">
                  {total.toFixed(2)} €
                </span>
              </div>

              <Button
                onClick={onCheckout}
                disabled={checkoutLoading || !selectedTier}
                size="lg"
                className="w-full rounded-full h-12"
                data-testid="btn-checkout"
              >
                {checkoutLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirection…</>
                ) : (
                  <><ShoppingCart className="h-4 w-4 mr-2" /> Commander sur Stripe</>
                )}
              </Button>
              <p className="text-[11px] text-center text-muted-foreground">
                Paiement sécurisé Stripe · TVA France 20% · Remboursement 14 j
              </p>
            </div>
          </Card>

          {/* Trust signals */}
          <div className="mt-5 grid grid-cols-3 gap-2 text-[11px]">
            <div className="text-center p-2"><ShieldCheck className="h-4 w-4 text-secondary mx-auto mb-1" /><span className="text-muted-foreground">Garantie 2 ans</span></div>
            <div className="text-center p-2"><Truck className="h-4 w-4 text-primary mx-auto mb-1" /><span className="text-muted-foreground">Suivi Colissimo</span></div>
            <div className="text-center p-2"><BookOpen className="h-4 w-4 text-amber-600 mx-auto mb-1" /><span className="text-muted-foreground">Notice PDF</span></div>
          </div>
        </div>
      </div>

      {/* Description + contenu */}
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-bold tracking-tight">Description détaillée</h2>
          <div className="prose prose-slate max-w-none mt-4">
            <ReactMarkdown>{kit.long_description_markdown || kit.short_description}</ReactMarkdown>
          </div>
        </div>
        <div>
          {(kit.included_parts || []).length > 0 && (
            <Card className="p-5">
              <h3 className="font-display font-bold text-lg">Contenu du kit</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {kit.included_parts.map((p, i) => (
                  <li key={i} className="flex justify-between gap-3 pb-2 border-b last:border-0">
                    <span>
                      <span className="font-semibold">{p.brand} — {p.ref}</span>
                      {p.notes && <span className="text-xs text-muted-foreground block">{p.notes}</span>}
                    </span>
                    <span className="text-muted-foreground shrink-0">×{p.qty}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {(kit.required_tools || []).length > 0 && (
            <Card className="p-5 mt-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Wrench className="h-4 w-4 text-primary" /> Outillage requis
              </h3>
              <ul className="mt-3 space-y-1 text-sm">
                {kit.required_tools.map((t, i) => (
                  <li key={i} className="text-muted-foreground">• {t}</li>
                ))}
              </ul>
            </Card>
          )}

          {kit.linked_tutorial_slug && (
            <Card className="p-5 mt-4 bg-primary/5 border-primary/20">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-primary" /> Tuto associé
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Consultez le tuto complet avant de commander pour vérifier que le kit correspond à votre besoin.
              </p>
              <Link to={`/tutos/${kit.linked_tutorial_slug}`}>
                <Button variant="outline" className="mt-3 rounded-full w-full" data-testid="btn-see-tuto">
                  Voir le tuto pas-à-pas →
                </Button>
              </Link>
            </Card>
          )}
        </div>
      </div>

      {/* Partage — booste la boutique */}
      <div className="mt-10 rounded-2xl border border-black/10 bg-stone-50 p-6">
        <p className="text-sm font-semibold">Un proche a le même spa ? Partagez-lui ce kit 🛁</p>
        <div className="mt-3">
          <ShareButtons title={kit.name} testIdPrefix="kit-share" contentType="kit" slug={kit.slug} />
        </div>
      </div>

      {/* Avis clients — achats vérifiés */}
      <KitReviews slug={slug} />
    </div>
  );
}
